import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function LanguageSwitcher({ className = '', align = 'right', variant = 'light' }) {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const languages = [
        { code: 'en', name: 'English', shortName: 'EN' },
        { code: 'ar', name: 'العربية', shortName: 'AR' },
        { code: 'es', name: 'Español', shortName: 'ES' }
    ];

    const currentLanguage = languages.find(lang => i18n.language.startsWith(lang.code)) || languages[0];

    const changeLanguage = (langCode) => {
        i18n.changeLanguage(langCode);
        localStorage.setItem('i18nextLngManual', 'true');
        setIsOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle keyboard navigation (Escape to close)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    const isDark = variant === 'dark';
    const textColor = isDark ? 'text-text' : 'text-white';
    const subTextColor = isDark ? 'text-text/80' : 'text-white/80';
    const hoverBg = isDark ? 'hover:bg-black/5' : 'hover:bg-white/10';

    return (
        <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md ${hoverBg} transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gold/50 group`}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                title="Change Language"
            >
                <span className={`text-sm font-medium tracking-wide uppercase ${textColor} drop-shadow-sm group-hover:text-gold transition-colors`}>{currentLanguage.shortName}</span>
                <svg
                    className={`w-3.5 h-3.5 ${subTextColor} transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} group-hover:text-gold`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div
                    className={`absolute top-full mt-2 w-40 p-1 bg-white border border-stone-200 shadow-xl rounded-lg z-50 transform transition-all duration-200 ease-out
                        ${align === 'left' ? 'left-0 origin-top-left' : 'right-0 origin-top-right'}
                    `}
                    role="listbox"
                >
                    {languages.map((lang) => {
                        const isActive = i18n.language.startsWith(lang.code);
                        return (
                            <button
                                key={lang.code}
                                onClick={() => changeLanguage(lang.code)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-md transition-colors
                                    ${isActive
                                        ? 'bg-gold/5 text-gold font-medium'
                                        : 'text-text hover:bg-bg-soft'
                                    }
                                `}
                                role="option"
                                aria-selected={isActive}
                            >
                                <span>{lang.name}</span>
                                <span className={`text-xs opacity-50 uppercase ${isActive ? 'text-gold' : ''}`}>
                                    {lang.shortName}
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
